import type { ReactNode } from "react";
import { TaskGroupBy, TaskPriority, TaskStatus } from "@common/types/task";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { getTaskColumnIcon } from "@components/tasks/board/taskBoardColumnIcons";
import { TASK_PRIORITY_CONFIG } from "@components/tasks/TaskPrioritySelect/taskPriority.constant";
import { TASK_STATUS_CONFIG } from "@components/tasks/TaskStatusSelect/taskStatus.constants";
import {
    DEMO_CLIENTS,
    DEMO_ENTITIES,
    DEMO_PEOPLE,
    type DemoTask,
} from "./fixtures/demoTasks";
import {
    getStageId,
    NO_STAGE_COLUMN_ID,
    TASK_STAGE_COLUMNS,
} from "./taskBoardConfig";

/** A task property the board can lay out as columns or as lanes. */
export type TaskAxis =
    | "stage"
    | "status"
    | "assignee"
    | "client"
    | "priority"
    | "entity";

export interface TaskAxisValue {
    id: string;
    title: string;
    icon?: ReactNode;
}

export const NO_VALUE_ID = "none";
const STATUSES = Object.values(TaskStatus);
const PRIORITIES = Object.values(TaskPriority);
export const getEntityValueId = (entityId: number) => `entity-${entityId}`;

/**
 * Every value of each axis in board order; "No stage" first, other "No …"
 * values last. Statuses and priorities carry the real board's header icons.
 */
export const TASK_AXIS_VALUES: Record<TaskAxis, readonly TaskAxisValue[]> = {
    stage: TASK_STAGE_COLUMNS.map(({ id, title }) => ({ id, title })),
    status: STATUSES.map((status) => ({
        id: status,
        title: TASK_STATUS_CONFIG[status].tooltip,
        icon: getTaskColumnIcon({
            groupBy: TaskGroupBy.STATUS,
            groupKey: status,
        }),
    })),
    assignee: [
        ...DEMO_PEOPLE.map((person) => ({
            id: person.id,
            title: person.name,
            icon: <UserAvatar user={person} size="xs" aria-hidden />,
        })),
        { id: NO_VALUE_ID, title: "No assignee" },
    ],
    client: [
        ...DEMO_CLIENTS.map(({ id, name }) => ({ id, title: name })),
        { id: NO_VALUE_ID, title: "No client" },
    ],
    priority: [
        ...PRIORITIES.map((priority) => ({
            id: priority,
            title: TASK_PRIORITY_CONFIG[priority].label,
        })),
        { id: NO_VALUE_ID, title: "No priority" },
    ].map((value) => ({
        ...value,
        icon: getTaskColumnIcon({
            groupBy: TaskGroupBy.PRIORITY,
            groupKey: value.id,
        }),
    })),
    entity: [
        ...DEMO_ENTITIES.map((entity) => ({
            id: getEntityValueId(entity.id),
            title: entity.name,
            icon: <EntityAvatar entity={entity} size="xs" aria-hidden />,
        })),
        { id: NO_VALUE_ID, title: "No entity" },
    ],
};

/** The task's value ids on an axis; several for entities, one copy each. */
export const getAxisValueIds = (task: DemoTask, axis: TaskAxis): string[] => {
    switch (axis) {
        case "stage":
            return [task.stageId ?? NO_STAGE_COLUMN_ID];
        case "status":
            return [task.status];
        case "assignee":
            return [task.assigneeId ?? NO_VALUE_ID];
        case "client":
            return [task.clientId ?? NO_VALUE_ID];
        case "priority":
            return [task.priority ?? NO_VALUE_ID];
        case "entity":
            return task.entityIds.length === 0
                ? [NO_VALUE_ID]
                : task.entityIds.map(getEntityValueId);
    }
};

const toValue = (valueId: string) => (valueId === NO_VALUE_ID ? null : valueId);

const getEntityId = (valueId: string | null) =>
    DEMO_ENTITIES.find(({ id }) => getEntityValueId(id) === valueId)?.id;

export interface TaskAxisChange {
    /** The copy's value before the move; null for a new task. */
    fromId: string | null;
    toId: string;
}

/**
 * Entity copies swap one value: the task drops the source entity and gains
 * the target one, unless it already has it. Dropping a copy on "No entity"
 * only removes that entity.
 */
const swapEntity = (task: DemoTask, { fromId, toId }: TaskAxisChange) => {
    const sourceId = getEntityId(fromId);
    const targetId = getEntityId(toId);
    const kept = task.entityIds.filter((id) => id !== sourceId);
    return targetId === undefined || kept.includes(targetId)
        ? kept
        : [...kept, targetId];
};

/** The task with its value on `axis` moved from one value to another. */
export const setAxisValue = (
    task: DemoTask,
    axis: TaskAxis,
    change: TaskAxisChange,
): DemoTask => {
    switch (axis) {
        case "stage":
            return { ...task, stageId: getStageId(change.toId) };
        case "status":
            return {
                ...task,
                status:
                    STATUSES.find((status) => status === change.toId) ??
                    task.status,
            };
        case "assignee":
            return { ...task, assigneeId: toValue(change.toId) };
        case "client":
            return { ...task, clientId: toValue(change.toId) };
        case "priority":
            return {
                ...task,
                priority:
                    PRIORITIES.find((priority) => priority === change.toId) ??
                    null,
            };
        case "entity":
            return { ...task, entityIds: swapEntity(task, change) };
    }
};
